import * as React from 'react';
import { format, addYears, subYears, setMonth, setYear, isSameYear, isBefore, isAfter, startOfYear, startOfMonth } from 'date-fns';
import type { CaptionProps } from 'react-day-picker';
import { useDayPicker, useNavigation } from 'react-day-picker';
import Button from '../Button/Button';
import Dropdown from '../Dropdown/Dropdown';

import ArrowLeftIcon from '../../../assets/icons/v2design/navigation/arrow-left.svg';
import ArrowRightIcon from '../../../assets/icons/v2design/navigation/arrow-right.svg';

type Props = CaptionProps & {
    dateFormat: string;
};

const CalendarCaption = (props: Props) => {
    const { goToMonth, nextMonth, previousMonth, currentMonth } = useNavigation();
    const dayPicker = useDayPicker();
    const { selected } = dayPicker;
    let fromDate = dayPicker.fromDate || subYears(startOfYear(new Date()), 20);
    let toDate = dayPicker.toDate || addYears(startOfYear(new Date()), 20);

    if (!dayPicker.fromDate && isBefore(currentMonth, fromDate)) {
        fromDate = startOfYear(currentMonth);
    } else if (!dayPicker.toDate && isAfter(currentMonth, toDate)) {
        toDate = startOfYear(addYears(currentMonth, 1));
    }

    const years = React.useMemo(() => {
        const years = [];
        for (let i = fromDate.getFullYear(); i <= toDate.getFullYear(); i++) {
            years.push({
                name: i.toString(),
                value: setYear(currentMonth, i).getTime()
            });
        }
        return years;
    }, [currentMonth, fromDate, toDate]);

    const months = React.useMemo(() => {
        const months = [];
        const startMonth = isSameYear(fromDate, currentMonth) ? startOfMonth(fromDate) : setMonth(currentMonth, 0);
        const endMonth = isSameYear(toDate, currentMonth) ? startOfMonth(toDate) : setMonth(currentMonth, 11);

        for (let i = startMonth.getMonth(); i <= endMonth.getMonth(); i++) {
            const month = setMonth(startMonth, i);
            months.push({
                name: format(month, 'MMMM'),
                value: month.getTime()
            });
        }

        return months;
    }, [currentMonth, fromDate, toDate]);

    return (
        <div className="calendar-input-header">
            {dayPicker.mode === 'range' && (
                <div className="calendar-input-header-inputs">
                    <Button buttonSize="small" buttonType="secondary" className="calendar-input-header-input">
                        {(selected as $TSFixMe)?.from ? format((selected as $TSFixMe).from, props.dateFormat) : ''}
                    </Button>
                    <div className="calendar-input-header-separator">To</div>
                    <Button buttonSize="small" buttonType="secondary" className="calendar-input-header-input">
                        {(selected as $TSFixMe)?.to ? format((selected as $TSFixMe).to, props.dateFormat) : ''}
                    </Button>
                </div>
            )}
            <div className="calendar-input-header-dates">
                <Button
                    buttonType="tertiary"
                    buttonSize="small"
                    icon={<ArrowLeftIcon />}
                    disabled={!previousMonth}
                    onClick={() => previousMonth && goToMonth(previousMonth)}
                />
                <div className="calendar-input-header__center">
                    <Dropdown
                        value={currentMonth.getTime()}
                        options={months}
                        onChange={(dateNumber) => {
                            goToMonth(new Date(dateNumber));
                        }}
                        className="calendar-input-dropdown"
                        menuClassName="calendar-input-dropdown-tooltip"
                    />
                    <Dropdown
                        value={currentMonth.getTime()}
                        options={years}
                        onChange={(dateNumber) => {
                            goToMonth(new Date(dateNumber));
                        }}
                        className="calendar-input-dropdown"
                        menuClassName="calendar-input-dropdown-tooltip"
                    />
                </div>
                <Button
                    buttonType="tertiary"
                    buttonSize="small"
                    icon={<ArrowRightIcon />}
                    disabled={!nextMonth}
                    onClick={() => nextMonth && goToMonth(nextMonth)}
                />
            </div>
        </div>
    );
};

export default CalendarCaption;
